// src/pages/Checkout.jsx

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../api/api";
import {
  FaTruck,
  FaCreditCard,
  FaChevronRight,
  FaChevronLeft,
  FaCheckCircle,
  FaPlus,
  FaLock,
} from "react-icons/fa";
// ✅ NEW: Import PaymentMethodForm
import PaymentMethodForm from "../user/pages/PaymentMethodForm"; // Assuming this is the correct relative path
// ✅ NEW: Import AddressForm
import AddressForm from "../user/pages/AddressForm"; // Assuming this is the correct relative path
import {
  SHIPPING_CONFIG_DEFAULTS,
  fetchShippingConfig,
} from "../config/shippingConfig";

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems } = useCart();
  const shippingCompanyName = t("shippingCompanyName", "Fast Cargo (Standard)");

  // --- State for Checkout Steps ---
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Data State ---
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [shippingCost, setShippingCost] = useState(0.0);
  const [shippingConfig, setShippingConfig] = useState(
    SHIPPING_CONFIG_DEFAULTS,
  );
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  // ✅ NEW: State for showing the 'Add New Card' form
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  // ✅ NEW: State for showing the 'Add New Address' form
  const [isAddingAddress, setIsAddingAddress] = useState(false);

  // --- Calculation State ---
  const subtotal = cartItems.reduce(
    (acc, item) =>
      acc + (item.Product ? parseFloat(item.Product.price) * item.quantity : 0),
    0,
  );

  const total = subtotal + shippingCost;
  const remainingForFreeShipping =
    shippingConfig.freeShippingThreshold - subtotal;
  const isFreeShipping = subtotal >= shippingConfig.freeShippingThreshold;

  // 1. Helper function to fetch addresses
  const fetchAddresses = async (initialLoad = false) => {
    try {
      const addressRes = await api.get("/user/addresses");
      setAddresses(addressRes.data);
      if (addressRes.data.length > 0 && initialLoad) {
        // Select the first address by default ONLY on initial load
        setSelectedAddressId(addressRes.data[0].address_id);
      } else if (
        addressRes.data.length > 0 &&
        !initialLoad &&
        !selectedAddressId
      ) {
        // If a new address was just added, select it if none is selected
        setSelectedAddressId(addressRes.data[0].address_id);
      }
    } catch (err) {
      console.error("Address fetch failed:", err);
      // Optional: Set an error state specific to address loading
    }
  };

  // 2. Helper function to fetch payment methods
  const fetchPaymentMethods = async (initialLoad = false) => {
    try {
      const paymentRes = await api.get("/user/payment-methods");
      setPaymentMethods(paymentRes.data);
      if (paymentRes.data.length > 0 && initialLoad) {
        // Select the first payment method by default ONLY on initial load
        setSelectedPaymentMethodId(paymentRes.data[0].payment_method_id);
      } else if (
        paymentRes.data.length > 0 &&
        !initialLoad &&
        !selectedPaymentMethodId
      ) {
        // If a saved card was just added, select it if none is selected
        setSelectedPaymentMethodId(paymentRes.data[0].payment_method_id);
      }
    } catch (err) {
      console.error("Payment method fetch failed:", err);
      // Optional: Set an error state specific to payment loading
    }
  };

  // 3. Fetch Data (Addresses and Payment Methods)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || cartItems.length === 0) {
      navigate("/cart");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Addresses and set initial selection
        await fetchAddresses(true);

        // Fetch Payment Methods and set initial selection
        await fetchPaymentMethods(true);

        const liveShippingConfig = await fetchShippingConfig();
        setShippingConfig(liveShippingConfig);

        // Calculate initial shipping cost
        const cost =
          subtotal >= liveShippingConfig.freeShippingThreshold
            ? 0.0
            : liveShippingConfig.standardShippingRate;
        setShippingCost(cost);
      } catch (err) {
        console.error("Checkout data fetch failed:", err);
        setError(
          t(
            "checkoutLoadError",
            "Failed to load checkout details. Please try again.",
          ),
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, cartItems, subtotal, t]);

  // Handle navigation between steps
  const nextStep = () => {
    // Basic validation before moving forward
    if (step === 1) {
      if (!selectedAddressId) return;
      // Also prevent moving forward if the add address form is open
      if (isAddingAddress) {
        setError(
          t(
            "saveAddressFirst",
            "Please save or cancel the new address form first.",
          ),
        );
        return;
      }
    }
    // Step 2 doesn't have a choice (currently just displays the single rate), so no validation needed
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  // ✅ NEW: Handlers for Address Form
  const handleSaveAddress = () => {
    setIsAddingAddress(false);
    fetchAddresses(); // Refresh and potentially select the new address
  };

  const handleCancelAddressForm = () => {
    setIsAddingAddress(false);
  };

  // ✅ NEW: Handlers for Payment Method Form
  const handleSavePayment = () => {
    setIsAddingPayment(false);
    fetchPaymentMethods(); // Refresh and potentially select the new method
  };

  const handleCancelPaymentForm = () => {
    setIsAddingPayment(false);
  };

  // 4. Handle Final Order Submission (UPDATED)
  const handlePlaceOrder = async () => {
    // 🚨 Ensure no forms are open before proceeding
    if (isAddingPayment) {
      setError(
        t("saveCardFirst", "Please save or cancel the new card form first."),
      );
      return;
    }

    if (!selectedAddressId || !selectedPaymentMethodId) {
      setError(
        t(
          "selectionError",
          "Please select both an address and a payment method.",
        ),
      );
      return;
    }

    setLoading(true); // Start loading state for the final submission
    setError(null);

    // Prepare the data payload for the backend
    const orderData = {
      // IDs must be sent to the backend
      shipping_address_id: selectedAddressId,
      payment_method_id: selectedPaymentMethodId,
      subtotal: subtotal.toFixed(2),
      shipping_cost: shippingCost, // Calculated on the frontend, but the backend re-calculates/verifies
      // Fix for total: ensure total is fixed to 2 decimal places when sending to backend
      total_amount: total.toFixed(2),

      // Send cart items for backend verification/OrderItems creation
      orderItems: cartItems.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.Product.price, // Price at the time of purchase
      })),

      // notes: "Please deliver before 5 PM",
    };

    try {
      // 🚨 POST the entire order and payment data to the new backend endpoint
      const response = await api.post("/orders/place", orderData);

      // ✅ CRITICAL UPDATE: Destructure the new field from the response
      const { order_id, message, payment, user_order_count } = response.data;

      // Success: Navigate to the order success page
      navigate(`/order/success/${order_id}`, {
        state: {
          message: message,
          transactionId: payment.transaction_id,
          // ✅ NEW VALUE: Pass the order count to the success page
          userOrderCount: user_order_count,
        },
      });
    } catch (err) {
      console.error("Order placement failed:", err);
      // ... (error handling logic remains the same) ...
      const errorMessage =
        err.response?.data?.message ||
        t(
          "genericOrderFail",
          "Order failed due to a payment issue or server error.",
        );

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">
          {t("loading", "Loading checkout...")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-10 p-6 rounded-lg shadow-md bg-red-100 text-red-700">
        {error}
      </div>
    );
  }

  // --- Step Components (StepIndicator is unchanged) ---

  const StepIndicator = ({ number, title, currentStep }) => (
    <div
      className={`flex flex-col items-center flex-1 ${
        number <= currentStep ? "text-[#83375B]" : "text-gray-400"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold mb-1 border-2 ${
          number === currentStep
            ? "bg-[#83375B] text-white border-[#83375B]"
            : number < currentStep
              ? "bg-[#E0BBE4] text-[#83375B] border-[#E0BBE4]"
              : "bg-white border-gray-400"
        }`}
      >
        {number < currentStep ? <FaCheckCircle className="w-4 h-4" /> : number}
      </div>
      <span className="text-xs text-center hidden sm:block">{title}</span>
    </div>
  );

  // 🔄 UPDATED: Render Address Selection
  const renderAddressSelection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">
        {t("selectAddress", "1. Select Shipping Address")}
      </h2>

      {isAddingAddress ? (
        // Show the form to add a new address
        <AddressForm
          onSave={handleSaveAddress}
          onCancel={handleCancelAddressForm}
          addressToEdit={null} // We are only adding, not editing here
        />
      ) : (
        // Show saved addresses or the 'Add New Address' button
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {addresses.length > 0 ? (
              addresses.map((address) => (
                <div
                  key={address.address_id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedAddressId === address.address_id
                      ? "border-[#83375B] ring-2 ring-[#83375B] bg-[#f9f5fa]"
                      : "border-gray-200 hover:border-[#AA7F96] bg-white"
                  }`}
                  onClick={() => setSelectedAddressId(address.address_id)}
                >
                  <h3 className="font-semibold capitalize text-gray-800">
                    {address.address_type || t("primaryAddressType", "Primary")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {address.address_line_1}
                  </p>
                  <p className="text-sm text-gray-600">
                    {address.city}, {address.postal_code}
                  </p>
                  <p className="text-sm text-gray-600">{address.country}</p>
                </div>
              ))
            ) : (
              <p className="text-red-500 col-span-2">
                {t(
                  "noAddress",
                  "No addresses found. Please add one to continue.",
                )}
              </p>
            )}
            {/* New Address Option - Always available */}
            <div
              className={`p-4 border rounded-lg cursor-pointer transition-all flex items-center justify-center text-center text-[#AA7F96] hover:bg-[#E0BBE4] border-dashed border-[#AA7F96] ${
                addresses.length > 0 ? "" : "col-span-2" // Take full width if no addresses exist
              }`}
              onClick={() => {
                setIsAddingAddress(true);
                setSelectedAddressId(null); // Deselect any existing address when adding a new one
              }}
            >
              <FaPlus className="mr-2" />
              {t("addNewAddress", "Add New Address")}
            </div>
          </div>

          {/* This button is now redundant and can be simplified or removed, using the card instead */}
          {/* <button
            onClick={() => navigate("/profile?tab=addresses")}
            className="text-[#AA7F96] hover:underline flex items-center text-sm"
          >
            <FaPlus className="mr-1" />{" "}
            {t("addEditAddress", "Add or Edit Addresses in Profile")}
          </button> */}
        </>
      )}
    </div>
  );

  const renderShippingMethod = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">
        {t("selectShipping", "2. Shipping Method")}
      </h2>

      {/* Shipping Rate Card */}
      <div className="p-4 border border-[#83375B] rounded-lg bg-[#f9f5fa] shadow-md">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold text-lg text-gray-800 flex items-center">
            <FaTruck className="mr-2 text-[#AA7F96]" /> {shippingCompanyName}
          </h3>
          <span className="font-bold text-xl text-[#83375B]">
            {shippingCost === 0.0 ? (
              <span className="text-green-600">{t("free", "FREE")}</span>
            ) : (
              `${shippingCost.toFixed(2)} TL`
            )}
          </span>
        </div>
        <p className="text-sm text-gray-600">
          {t("deliveryTime", "Estimated delivery: 3-5 business days.")}
        </p>

        {/* Free Shipping Eligibility Message */}
        <div className="mt-3 pt-3 border-t border-[#E0BBE4]">
          {isFreeShipping ? (
            <p className="text-sm font-medium text-green-600">
              <FaCheckCircle className="inline-block mr-1" />
              {t("freeShippingQualified", "You qualified for FREE shipping!")}
            </p>
          ) : (
            <p className="text-sm font-medium text-gray-500">
              {t(
                "freeShippingGoal",
                "Spend {{amount}} TL more to get FREE shipping (over {{threshold}} TL).",
                {
                  amount: remainingForFreeShipping.toFixed(2),
                  threshold: shippingConfig.freeShippingThreshold,
                },
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // src/pages/Checkout.jsx

  // 🔄 UPDATED: Render Payment Method
  const renderPaymentMethod = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">
        {t("selectPayment", "3. Select Payment Method")}
      </h2>

      {isAddingPayment ? (
        // Show the form to add a new card
        <PaymentMethodForm
          onSave={handleSavePayment}
          onCancel={handleCancelPaymentForm}
          // We pass null for editing since we are only adding a new one
          methodToEdit={null}
        />
      ) : (
        // Show saved cards or the 'Add New Card' button
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paymentMethods.length > 0 &&
              paymentMethods.map((method) => (
                <div
                  key={method.payment_method_id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                    selectedPaymentMethodId === method.payment_method_id
                      ? "border-[#83375B] ring-2 ring-[#83375B] bg-[#f9f5fa]"
                      : "border-gray-200 hover:border-[#AA7F96] bg-white"
                  }`}
                  onClick={() =>
                    setSelectedPaymentMethodId(method.payment_method_id)
                  }
                >
                  <div className="flex items-center space-x-3">
                    <FaCreditCard className="text-gray-500 w-6 h-6" />
                    <span className="font-medium text-gray-800">
                      {/* ✅ FIX: Removed .details */}
                      {t("cardEndingIn", "{{brand}} ending in {{last4}}", {
                        brand: method.card_brand || method.type,
                        last4: method.last4,
                      })}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {/* ✅ FIX: Removed .details and ensured leading zero for month */}
                    {t("cardExpLabel", "Exp")}:{" "}
                    {String(method.exp_month).padStart(2, "0")}/
                    {method.exp_year}
                  </span>
                </div>
              ))}
            {/* Fallback/New Card Option - Always available */}
            <div
              className={`p-4 border rounded-lg cursor-pointer transition-all flex items-center justify-center text-center text-[#AA7F96] hover:bg-[#E0BBE4] border-dashed border-[#AA7F96] ${
                paymentMethods.length > 0 ? "" : "md:col-span-2" // Take full width if no cards
              }`}
              onClick={() => {
                setIsAddingPayment(true);
                setSelectedPaymentMethodId(null); // Deselect any existing card when adding a new one
              }}
            >
              <FaPlus className="mr-2" />
              {t("addCard", "Add New Card / Pay with Different Card")}
            </div>

            {paymentMethods.length === 0 && (
              <p className="text-red-500 col-span-2 mt-4">
                {t(
                  "noPaymentMethod",
                  "No saved payment methods. Please add a new card.",
                )}
              </p>
            )}
          </div>

          {/* Note for the user about the payment process */}
          <p className="text-sm text-gray-500 mt-4 p-3 bg-gray-100 rounded-md">
            <FaLock className="inline-block mr-1 text-green-500" />
            {t(
              "securePaymentNote",
              "By clicking 'Finish Payment', you agree to charge the selected card/method. If you selected a new card, you will be prompted for full details on the secure gateway.",
            )}
          </p>
        </>
      )}
    </div>
  );

  // --- Main Render (Navigation logic updated to check for isAddingAddress) ---

  return (
    <div className="canvas-background mx-auto p-4 sm:p-6 lg:px-[11rem] lg:py-[8rem] min-h-screen">
      <h1 className="text-4xl font-extrabold text-[#83375B] mb-8 border-b-4 border-[#E0BBE4] pb-2">
        {t("checkoutTitle", "Checkout")}
      </h1>

      {/* Step Indicator */}
      <div className="flex justify-between items-center mb-10 px-4">
        <StepIndicator
          number={1}
          title={t("stepAddress", "Shipping Address")}
          currentStep={step}
        />
        <div className="h-1 w-full bg-gray-300 mx-2 mt-4" />
        <StepIndicator
          number={2}
          title={t("stepShipping", "Shipping Method")}
          currentStep={step}
        />
        <div className="h-1 w-full bg-gray-300 mx-2 mt-4" />
        <StepIndicator
          number={3}
          title={t("stepPayment", "Payment")}
          currentStep={step}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left/Main Column: Step Content */}
        <div className="lg:col-span-2 bg-white rounded-xl p-8 shadow-xl">
          {step === 1 && renderAddressSelection()}
          {step === 2 && renderShippingMethod()}
          {step === 3 && renderPaymentMethod()}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-8 border-t mt-8">
            {step > 1 && (
              <button
                onClick={prevStep}
                className="flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                // Disable back if we are in the middle of adding a new address or payment method
                disabled={isAddingAddress || isAddingPayment}
              >
                <FaChevronLeft className="mr-2" /> {t("back", "Back")}
              </button>
            )}

            {step < 3 && (
              <button
                onClick={nextStep}
                disabled={
                  (step === 1 && (!selectedAddressId || isAddingAddress)) || // Disable if no address selected OR adding a new address
                  (step === 2 && isAddingAddress) // Should technically not happen as address is step 1, but for safety
                }
                className={`flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white transition-colors ${
                  (step === 1 && (!selectedAddressId || isAddingAddress)) ||
                  isAddingPayment
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#AA7F96] hover:bg-[#916A80]"
                } ${step === 1 && "ml-auto"}`}
              >
                {t("continue", "Continue")} <FaChevronRight className="ml-2" />
              </button>
            )}

            {step === 3 && (
              <button
                onClick={handlePlaceOrder}
                // Disable button if no payment method is selected or if the add card form is open
                disabled={!selectedPaymentMethodId || isAddingPayment}
                className={`ml-auto flex items-center px-8 py-4 text-lg font-bold rounded-xl text-white transition-colors shadow-lg ${
                  !selectedPaymentMethodId || isAddingPayment
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#83375B] hover:bg-[#6A2B47]"
                }`}
              >
                <FaCreditCard className="mr-2 ml-2" />{" "}
                {t("finishPayment", "Finish Payment")}
              </button>
            )}
          </div>
        </div>

        {/* Right Column: Order Summary (unchanged) */}
        <div className="lg:col-span-1 bg-gray-50 rounded-xl p-6 shadow-xl sticky top-20 h-fit border-t-4 border-[#AA7F96]">
          <h2 className="text-2xl font-bold text-[#83375B] mb-6">
            {t("orderSummary", "Order Summary")}
          </h2>

          <div className="space-y-2 border-b pb-4 mb-4 max-h-60 overflow-y-auto">
            {cartItems.map((item) => (
              <div
                key={item.cart_item_id}
                className="flex justify-between text-sm text-gray-600"
              >
                <span className="truncate w-3/4">
                  {item.Product[`name_${t("language")}`] ||
                    item.Product.name_en}{" "}
                  ({item.quantity})
                </span>
                <span className="font-medium text-gray-800">
                  {(parseFloat(item.Product.price) * item.quantity).toFixed(2)}{" "}
                  TL
                </span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="space-y-3">
            <div className="flex justify-between font-medium text-gray-700">
              <span>{t("subtotal", "Subtotal")}:</span>
              <span>{subtotal.toFixed(2)} TL</span>
            </div>

            <div className="flex justify-between font-medium text-gray-700">
              <span>{t("shippingCost", "Shipping Cost")}:</span>
              <span
                className={
                  shippingCost === 0.0
                    ? "text-green-600 font-bold"
                    : "text-gray-800"
                }
              >
                {shippingCost === 0.0
                  ? t("free", "FREE")
                  : `${shippingCost.toFixed(2)} TL`}
              </span>
            </div>

            <div className="flex justify-between text-2xl font-extrabold text-gray-900 pt-4 border-t-2 border-[#E0BBE4]">
              <span>{t("orderTotal", "Order Total")}</span>
              <span>{total.toFixed(2)} TL</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
